// JsonEditor.tsx
import React, { useCallback, useEffect, useRef } from "react";
import Editor, { OnMount } from "@monaco-editor/react";

/** tiny stable hash for class names */
function simpleHash(s: string) {
  let h = 5381;
  for (let i = 0; i < s.length; i++) {
    // eslint-disable-next-line no-bitwise
    h = (h * 33) ^ s.charCodeAt(i);
  }
  return (h >>> 0).toString(36);
}

export interface JsonEditorProps {
  value?: string;
  defaultValue?: string;
  editable?: boolean;
  onChange?: (value: string | undefined) => void;
  onMount?: OnMount;
  backgroundColor?: string;
  height?: string;
  baseTheme?: "vs-dark" | "vs" | "hc-black";
  count?: any
}

/**
 * JsonEditor component with per-instance background and no outline/border.
 */
const JsonEditor: React.FC<JsonEditorProps> = ({
  value,
  defaultValue = "{}",
  editable = true,
  onChange,
  onMount,
  backgroundColor = "#1e1e1e",
  height = "35vh",
  baseTheme = "vs-dark",
  count = 1
}) => {


  const uidRef = useRef<string>(
    `jsoneditor_${simpleHash(String(backgroundColor))}_${Math.random().toString(36).slice(2, 8)}`
  );
  const styleRef = useRef<HTMLStyleElement | null>(null);
  const editorRef = useRef<any>(null);

  // NEW: keep references to the message controller and its original showMessage
  const messageControllerRef = useRef<any | null>(null);
  const originalShowMessageRef = useRef<((...args: any[]) => void) | null>(null);

  const handleEditorMount: OnMount = useCallback(
    (editor, monaco) => {
        editorRef.current = editor;
      // keep token colors from base theme
      try {
        monaco.editor.setTheme(baseTheme);
        
      } catch (e) {
        // ignore
      }

      const cls = uidRef.current;
      const bg = backgroundColor === "transparent" ? "transparent" : backgroundColor;

      // Scoped CSS:
      const css = `
/* Scoped background + border/shadow reset for Monaco inside .${cls} */
.${cls} .monaco-editor,
.${cls} .monaco-editor .overflow-guard,
.${cls} .monaco-editor .monaco-scrollable-element,
.${cls} .monaco-editor .view-lines,
.${cls} .monaco-editor .lines-content,
.${cls} .monaco-editor .margin,
.${cls} .monaco-editor .glyph-margin,
.${cls} .monaco-editor .margin-view-overlays,
.${cls} .monaco-editor .view-overlays,
.${cls} .monaco-editor .monaco-editor-background,
.${cls} .monaco-editor .inputarea {
  background: ${bg} !important;
  background-color: ${bg} !important;

  /* remove any visual border/shadow that may appear around editor */
  box-shadow: none !important;
  -webkit-box-shadow: none !important;
  outline: none !important;
  border: none !important;
}

/* Remove focus outline / shadow when Monaco or its inner elements receive focus */
.${cls} .monaco-editor:focus,
.${cls} .monaco-editor :focus,
.${cls} .monaco-editor .overflow-guard:focus,
.${cls} .monaco-editor .view-overlays:focus,
.${cls} .monaco-editor .inputarea:focus {
  outline: none !important;
  box-shadow: none !important;
  -webkit-box-shadow: none !important;
  border: none !important;
}

/* Also remove any decorative border on the editor's root element */
.${cls} .monaco-editor,
.${cls} .monaco-editor .monaco-editor-background {
  border-radius: inherit !important; /* ensure rounded corners match wrapper */
}

/* Optional: remove the default zone-widget light border, if present */
.${cls} .zone-widget,
.${cls} .monaco-editor .zone-widget {
  border: none !important;
  box-shadow: none !important;
}

/* If you're seeing an inner blue ring from outline offset, also target ::before/::after */
.${cls} .monaco-editor::before,
.${cls} .monaco-editor::after {
  box-shadow: none !important;
  outline: none !important;
}
`;



      // inject style once per instance
      if (!styleRef.current) {
        const styleEl = document.createElement("style");
        styleEl.type = "text/css";
        styleEl.appendChild(document.createTextNode(css));
        document.head.appendChild(styleEl);
        styleRef.current = styleEl;
      }

      // --- NEW: suppress the "Cannot edit in read-only editor" bubble for read-only editors ---
      // We only patch when the editor is read-only (editable === false).
      // Wrap in try/catch because this touches an internal contribution.
      // Disable Cmd+F / Ctrl+F find widget so it can't be opened accidentally
      try {
        editor.addCommand(monaco.KeyMod.CtrlCmd | monaco.KeyCode.KeyF, () => { /* noop */ });
      } catch (e) {
        // ignore if monaco internals changed
      }

      if (!editable) {
        try {
          // `getContribution` returns the internal controller instance for the editor.
          // We store it so we can restore its function later.
          // Use `any` to avoid TypeScript errors on internal API.
          const contrib = (editor as any).getContribution?.("editor.contrib.messageController");
          if (contrib && typeof contrib.showMessage === "function") {
            // store original
            messageControllerRef.current = contrib;
            originalShowMessageRef.current = contrib.showMessage;
            // override with noop
            contrib.showMessage = () => {
              /* noop - suppress default read-only tooltip */
            };
          } else {
            // In some Monaco builds the contribution key might differ; attempt alternative access:
            // (no-op, keep safe)
          }
        } catch (err) {
          // If internals changed, don't crash; just skip patching.
          // eslint-disable-next-line no-console
          console.warn("Could not silence Monaco read-only messageController", err);
        }
      }

      if (onMount) onMount(editor, monaco);

      // cleanup when editor disposes
      const disposeListener = editor.onDidDispose(() => {
        // restore original message function if we overrode it
        try {
          if (messageControllerRef.current && originalShowMessageRef.current) {
            try {
              messageControllerRef.current.showMessage = originalShowMessageRef.current;
            } catch (e) {
              // ignore if restore fails
            }
            messageControllerRef.current = null;
            originalShowMessageRef.current = null;
          }
        } catch (e) {
          // ignore any errors during restore
        }

        if (styleRef.current) {
          try {
            styleRef.current.remove();
          } catch (e) {
            // ignore
          }
          styleRef.current = null;
        }
        disposeListener.dispose();
      });
    },
    [backgroundColor, baseTheme, editable, onMount]
  );


  return (
    <div
      onMouseDown={(e) => { e.stopPropagation() }}
      key={count}
      className={uidRef.current}
      style={{
        overflow: "hidden",
        borderRadius: 4,
        background: backgroundColor === "transparent" ? "transparent" : backgroundColor,
        display: "flex",   
        height: '36vh'
      }}
      // accessibility hint (optional but recommended)
      aria-label={editable ? "Editable JSON editor" : "Read-only JSON editor"}
    >
      <Editor
        height="100%"
        defaultLanguage="json"
        language="json"
        theme={baseTheme}
        {...(value !== undefined ? { value } : { defaultValue })}
        onMount={handleEditorMount}
        onChange={editable ? onChange : undefined}
        options={{
          readOnly: !editable,
          minimap: { enabled: false },
          formatOnPaste: false,
          formatOnType: false,
          wordWrap: "on",
          tabSize: 2,
          scrollBeyondLastLine: false,
          lineNumbers: "off",
          scrollbar: {
            verticalScrollbarSize: 5,
            horizontalScrollbarSize: 5,
          },
          contextmenu: false,
          padding: {
            top: 10,
            bottom: 10,
          },
        }}
      />
    </div>
  );
};

export default JsonEditor;
